import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store.ts'
import { fetchIntegrationsList } from '../../reducers/integrations/integrations.thunk.ts'
import { useSelector } from 'react-redux'
import { selectIntegrations } from '../../reducers/integrations/integrations.slice.ts'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { IntegrationDetails } from '../../types/IntegrationTypes.ts'
import { PolicyActionsInput } from '../../types/SettingsType.ts'

interface IPolicyActionsProps {
  onActionDetailsSelected: (details: PolicyActionsInput) => void
  existingActionDetails?: PolicyActionsInput
}

function PolicyActions(props: IPolicyActionsProps) {
  const { onActionDetailsSelected, existingActionDetails } = props
  const di = useAppDispatch()
  const { integrationsList } = useSelector(selectIntegrations)
  const { darkModeEnabled } = useSelector(selectUser)

  const [configuredIntegrations, setConfiguredIntegrations] = useState<IntegrationDetails[]>([])
  const [availableIntegrations, setAvailableIntegrations] = useState<IntegrationDetails[]>([])
  const [threatLevel, setThreatLevel] = useState<string>('LOW')
  const [gracePeriodUnit, setGracePeriodUnit] = useState<string>('DAYS')
  const [gracePeriodInput, setGracePeriodInput] = useState<string>('0')
  const [hasHydrated, setHasHydrated] = useState(false)

  useEffect(() => {
    getIntegrationList()
  }, [])

  const getIntegrationList = async () => {
    await di(fetchIntegrationsList())
  }

  useEffect(() => {
    if (configuredIntegrations.length > 0) {
      const integration = configuredIntegrations[0]
      const timeout = setTimeout(() => {
        onActionDetailsSelected({
          threatLevel,
          gracePeriodValue: gracePeriodInput,
          gracePeriodUnit,
          integrationName: integration.name,
        })
      }, 200)

      return () => clearTimeout(timeout)
    } else {
      const timeout = setTimeout(() => {
        onActionDetailsSelected({
          threatLevel: 'LOW',
          gracePeriodValue: '',
          gracePeriodUnit: 'DAYS',
          integrationName: 'None',
        })
      })
      return () => clearTimeout(timeout)
    }
  }, [threatLevel, gracePeriodInput, gracePeriodUnit, configuredIntegrations])

  useEffect(() => {
    if (existingActionDetails && !hasHydrated) {
      const { threatLevel, gracePeriodValue, gracePeriodUnit, integrationName } = existingActionDetails

      if (threatLevel) setThreatLevel(threatLevel)
      if (gracePeriodValue) setGracePeriodInput(gracePeriodValue)
      if (gracePeriodUnit) setGracePeriodUnit(gracePeriodUnit)

      if (integrationName && configuredIntegrations.length > 0) {
        const match = configuredIntegrations.find((i) => i.name === integrationName)
        if (match) setConfiguredIntegrations([match])
      }

      setHasHydrated(true)
    }
  }, [existingActionDetails, configuredIntegrations, hasHydrated])

  useEffect(() => {
    if (integrationsList) {
      const activePage = integrationsList.find((item) => item.pageState === 'ACTIVE')
      const inactivePage = integrationsList.find((item) => item.pageState === 'INACTIVE')

      if (activePage) {
        const filteredActive = activePage.integrations.filter(
          (integration: IntegrationDetails) => integration.category === 'Conditional Access'
        )
        setConfiguredIntegrations(filteredActive)
      } else {
        setConfiguredIntegrations([])
      }

      if (inactivePage) {
        const filteredInactive = inactivePage.integrations.filter(
          (integration: IntegrationDetails) => integration.category === 'Conditional Access'
        )
        setAvailableIntegrations(filteredInactive)
      } else {
        setAvailableIntegrations([])
      }
    }
  }, [integrationsList])

  const buildIntegrationCard = (integration: IntegrationDetails, cardType: string) => {
    return (
      <div className="w-full px-4 py-2 bg-white rounded-lg outline outline-1 outline-mainBorder mx-auto">
        <div className="flex justify-between">
          <div className="flex">
            <img
              src={`${integration.image == '/okta.png' && darkModeEnabled ? '/okta-dark.png' : integration.image}`}
              alt="integration logo"
              className="w-[34px] h-[34px] mr-2"
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement
                if (target.src !== window.location.origin + '/default_logo.png') {
                  target.src = '/default_logo.png'
                }
              }}
            />
            <div className="block max-w-[460px]">
              <div className="justify-center text-mainText font-medium mt-1">{integration.name}</div>
            </div>
          </div>
          <div className="flex mt-0.5 gap-2">
            <>
              <div
                className="h-8 px-3 font-medium text-sm items-center rounded border border-neutral-5 text-paginationSelected cursor-pointer justify-center flex align-middle"
                onClick={() => window.open(integration.docsLink, '_blank')}
              >
                Read Doc
              </div>
            </>
          </div>
        </div>
        {integration.serverValue === 'intune_mtd' && cardType === 'CONFIGURED' && (
          <>
            <hr className="w-full h-px bg-mainBorder my-4" />
            <div className="w-full px-4 py-2 bg-surfaceSecondary rounded-lg outline outline-1 outline-mainBorder mx-auto flex justify-between">
              <div className="block max-w-[460px]">
                <div className="justify-center text-sm text-mainText font-medium">Set Threat Level</div>
                <p className="text-sm text-textTertiary text-center truncate">
                  The level of threat assigned to this policy
                </p>
              </div>
              <select
                className="h-[36px] w-[130px] bg-inputBackground flex border-mainBorder border rounded px-2 py-1.5 mt-0.5 justify-between text-sm focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple not-form-styled"
                value={threatLevel}
                onChange={(event) => {
                  setThreatLevel(event.target.value)
                }}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="w-[800px] p-4 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder mx-auto flex-col">
        <div className="justify-center text-mainText font-semibold">
          {configuredIntegrations.length > 0 ? 'Connected Integration' : 'Set a connected Integration (optional)'}
        </div>
        <div className="justify-center text-textTertiary text-sm">
          Report to the integration when a device comes out of policy.
        </div>
        <hr className="w-full h-px bg-mainBorder my-4" />
        {configuredIntegrations.length > 0 && (
          <div className="flex flex-col gap-2">
            {configuredIntegrations.map((integration: IntegrationDetails) => (
              <div key={integration.name}>{buildIntegrationCard(integration, 'CONFIGURED')}</div>
            ))}
          </div>
        )}
        {configuredIntegrations.length === 0 && (
          <>
            <p className="text-sm text-textTertiary text-center font-medium">You have no connected integration</p>
            <div className="w-[420px] text-center mx-auto leading-none mt-1">
              <span className="text-textTertiary text-xs">
                Connect an integration by configuring one of the available option below on the integrations page. If you
                do not wish to set up any connected integrations at this stage, continue to the next step.
              </span>
            </div>
          </>
        )}
      </div>

      <div className="w-[800px] p-4 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder mx-auto flex-col">
        <div className="justify-center text-mainText font-semibold">Available Integrations</div>
        <hr className="w-full h-px bg-mainBorder my-4" />
        {availableIntegrations.length > 0 && (
          <div className="flex flex-col gap-2">
            {availableIntegrations.map((integration: IntegrationDetails) => (
              <div key={integration.name}>{buildIntegrationCard(integration, 'AVAILABLE')}</div>
            ))}
          </div>
        )}
        {availableIntegrations.length === 0 && (
          <p className="text-sm text-textTertiary text-center font-medium">You have no available integrations</p>
        )}
      </div>
    </div>
  )
}

export default PolicyActions
